import React, { useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HERO_CONTENT, ANIMATION_VARIANTS } from '../../constants';
import { 
  useScrollIndicator,
  useOptimizedBackground,
  usePerformanceMonitoring
} from '../../hooks/useAnimations';
import { handleSectionNavigation, handlePageNavigation } from '../../utils/navigation';

const HeroSection: React.FC = () => {
  usePerformanceMonitoring('HeroSection');
  
  const { containerClasses: scrollIndicatorClasses, innerClasses: scrollInnerClasses, dotClasses: scrollDotClasses } = useScrollIndicator();
  const { backgroundClasses, backgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8))'
  );
  const scrollToServicesSection = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    handleSectionNavigation('Services', e);
  }, []);

  // Preload images for better performance
  useEffect(() => {
    const imagePaths = [
      HERO_CONTENT.backgroundImage
    ];
    
    // Preload images
    imagePaths.forEach(path => {
      const img = new Image();
      img.src = path;
    });
  }, []);

  return (
    <section className="min-h-screen flex items-center relative justify-center overflow-hidden">
      {/* Optimized background image */}
      <div 
        className={`${backgroundClasses} absolute inset-0`}
        style={{...backgroundStyle}}
      />

      {/* Content Container */}
      <div className="container relative z-40 text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={ANIMATION_VARIANTS.staggerChildren}
          className="max-w-4xl mx-auto"
        >
          <motion.h3 variants={ANIMATION_VARIANTS.slideUp}>
            <span className="text-sm md:text-base mb-8 text-gray-300 font-medium block tracking-[0.2em] uppercase relative">
              <span 
                className="bg-gradient-to-r from-gray-400 via-primary-300 to-gray-400 bg-clip-text text-transparent opacity-80"
                style={{
                  animation: 'futuristicGlow 4s ease-in-out infinite'
                }}
              >
                {HERO_CONTENT.subtitle}
              </span>
              {/* Subtle futuristic underline */}
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-400 to-transparent opacity-40"></span>
              {/* Small side dots */}
              <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-1 h-1 bg-primary-400 rounded-full opacity-60"></span>
              <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-1 h-1 bg-primary-400 rounded-full opacity-60"></span>
            </span>
          </motion.h3>

          {/* Main Title */}
          <motion.h1
            variants={ANIMATION_VARIANTS.slideUp}
            className="heading-1 mb-6"
          >
            {HERO_CONTENT.title}
            <br></br>
            <span className="text-primary-gradient block lg:inline">
              Cloud & AI Innovation 
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            variants={ANIMATION_VARIANTS.slideUp}
            className="hero-subtitle mb-8 max-w-3xl mx-auto"
          >
            {HERO_CONTENT.description}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={ANIMATION_VARIANTS.slideUp}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to={HERO_CONTENT.ctaLink}
              className="btn-futuristic"
              onClick={scrollToServicesSection}
            >
              {HERO_CONTENT.ctaText}
            </Link>
            
            <Link
              to="/contact"
              className="btn-outline"
              onClick={handlePageNavigation}
            >
              Contact Us
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Optimized Scroll Down Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.5 }}
        className={scrollIndicatorClasses}
        style={{
          animation: 'scrollBounce 2s ease-in-out infinite'
        }}
      >
        <div className={scrollInnerClasses}>
          <div 
            className={scrollDotClasses}
            style={{
              animation: 'scrollDot 2s ease-in-out infinite'
            }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default React.memo(HeroSection);
